/**
 * useChannelMap · 渠道元信息映射 Hook
 *
 * - 通过 cond 接口拉渠道字典（默认 `materials_stats_config_channels`，支持自定义）
 * - 返回 `{ channel_id → IChannelMeta }` 映射 + options 数组
 * - 供 ChannelCompare / Creative Trends 等模块共用
 *
 * ⚠️ 在主页面 `index.tsx` 调用一次，结果通过 props 传给子模块，
 *   避免每个模块重复请求 / 维护副本。
 *
 * Cond key 演进：
 *  - 旧：`materials_config_channels`（仅 value/label_en/label_zh）
 *  - 新：`materials_stats_config_channels`（同结构，可能含更多统计相关元数据）
 *  传入 `requestKey` 可指定使用哪个；默认走新 key。
 */
import { useMemo } from 'react';
import { useFilterCondData } from '@opm/components/FilterCommon/hooks/useFilterCondData';
import { TOptionsItem } from '@opm/components/FilterCommon/types';
import { channelInfo } from '@op/components/ChannelLabel';

/** cond 接口返回的渠道项原始结构（参考 dealwithCondOptions） */
interface ICondChannelRaw {
  value: string; // tiktok / facebook / ...
  label_en: string; // TikTok / Facebook / ...
  label_zh?: string;
  /** 新接口可能下发的额外字段（icon / category / order 等），统一透传 */
  [key: string]: any;
}

/** 支持的 cond requestKey */
export type TChannelCondKey =
  | 'materials_stats_config_channels'
  | 'materials_config_channels';

/** channelMap 单项（继承 cond raw 字段，便于业务侧按需取扩展属性） */
export interface IChannelMeta extends TOptionsItem {
  /** 原始 cond 数据（透传，业务侧可按需读取） */
  raw?: ICondChannelRaw;
}

export type TChannelMap = Record<string, IChannelMeta>;

export interface IUseChannelMapReturn {
  /** id → meta 映射 */
  channelMap: TChannelMap;
  /** 全部渠道列表（按 cond 顺序，不含 "all" 占位） */
  channels: IChannelMeta[];
  loading: boolean;
  /** 全部渠道 id 列表（按 cond 顺序） */
  allChannelIds: string[];
}

export function useChannelMap(
  requestKey: TChannelCondKey = 'materials_stats_config_channels',
): IUseChannelMapReturn {
  const { condData, loading } = useFilterCondData({
    keys: [],
    extrRequestKeys: [requestKey],
  });

  const { channels, allChannelIds, channelMap } = useMemo<{
    allChannelIds: string[];
    channels: IChannelMeta[];
    channelMap: TChannelMap;
  }>(() => {
    // 优先取请求的 key；若新 key 缺数据，兼容旧 key 兜底
    const raw: ICondChannelRaw[] =
      condData?.[requestKey] || condData?.materials_config_channels || [];
    const allIds: string[] = [];
    const m: TChannelMap = {};
    const channels = raw
      .filter(c => c?.value)
      .map(c => {
        const value = c.value?.toLocaleLowerCase();
        const { name } = channelInfo(value as any, undefined, true);
        allIds.push(value);

        const item: IChannelMeta = {
          value,
          text: name,
          textCn: name,
          raw: c,
        };
        m[value] = item;

        return item;
      });

    return {
      channels,
      allChannelIds: allIds,
      channelMap: m,
    };
  }, [condData, requestKey]);

  return { channelMap, channels, loading, allChannelIds };
}
